import { Chat, type Chat as ChatInstance, type User } from '@pubnub/chat';
import { create } from 'zustand';
import {
  PUBNUB_HEARTBEAT_INTERVAL,
  PUBNUB_PRESENCE_TIMEOUT,
  PUBNUB_PUBLISH_KEY,
  PUBNUB_SUBSCRIBE_KEY,
} from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { useConversationsStore } from './conversations';
import { useMessagesStore } from './messages';

interface ChatStore {
  chat: ChatInstance | null;
  currentUser: User | null;
  isInitialized: boolean;
  error: string | null;
  initChat: (userId: string, displayName?: string) => Promise<void>;
  reset: () => void;
  disconnectAndReset: () => Promise<void>;
}

const initialState = {
  chat: null,
  currentUser: null,
  isInitialized: false,
  error: null,
};

const DISCONNECT_TIMEOUT_MS = 1500;

export const useChatStore = create<ChatStore>()((set, get) => ({
  ...initialState,

  initChat: async (userId: string, displayName?: string): Promise<void> => {
    if (get().isInitialized) return;
    try {
      const chatInstance = await Chat.init({
        publishKey: PUBNUB_PUBLISH_KEY,
        subscribeKey: PUBNUB_SUBSCRIBE_KEY,
        userId,
        typingTimeout: 5000,
        presenceTimeout: PUBNUB_PRESENCE_TIMEOUT,
        heartbeatInterval: PUBNUB_HEARTBEAT_INTERVAL,
      });

      if (displayName) {
        await chatInstance.updateUser(userId, { name: displayName });
      }

      set({
        chat: chatInstance,
        currentUser: chatInstance.currentUser,
        isInitialized: true,
        error: null,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : t('ChatStore.error', { ns: LOCIZE_NS.CHAT }),
        isInitialized: false,
      });
    }
  },

  reset: (): void => set(initialState),

  disconnectAndReset: async (): Promise<void> => {
    const { chat } = get();
    if (chat) {
      try {
        await Promise.race([
          chat.disconnectSubscriptions(),
          new Promise<void>(resolve => setTimeout(resolve, DISCONNECT_TIMEOUT_MS)),
        ]);
      } catch {
        // eslint-disable-next-line no-console
        console.error('Failed to disconnect from PubNub');
      }
    }

    set(initialState);
    useConversationsStore.setState({ conversations: new Map(), activeChannelId: null });
    useMessagesStore.setState({ messages: new Map(), pinnedMessages: new Map() });
  },
}));
